import { NEXT_YEAR_EARLY_REGISTRATION_TYPE, REGISTRATION_TYPE_ATTENDEE_FROM_JAPAN_OR_KOREA, REGISTRATION_TYPE_DEFAULT, REGISTRATION_TYPE_REGISTRAR, REGISTRATION_TYPE_STAFF, REGISTRATION_TYPE_STUDENT, REGISTRATION_TYPE_TRIAL_COORDINATOR } from '@/constants/constants';
import IsNextYearEarlyRegistrationContext from '@/contexts/IsNextYearEarlyRegistrationContext';
import { useScopedField } from '@ngt/forms-core';
import { useContext, useEffect } from 'react';

const subscription = {
    value: true
};

const RegistrationTypeUpdater = () => {
    const isNextYearEarlyRegistration = useContext(IsNextYearEarlyRegistrationContext);

    const {
        state: { value: defaultUser },
        context: { setValue: setDefaultUser }
    } = useScopedField<boolean>('defaultUser', subscription);

    const {
        state: { value: student },
        context: { setValue: setStudent }
    } = useScopedField<boolean>('student', subscription);

    const {
        state: { value: trialCoordinator },
        context: { setValue: setTrialCoordinator }
    } = useScopedField<boolean>('trialCoordinator', subscription);

    const {
        state: { value: registrar },
        context: { setValue: setRegistrar }
    } = useScopedField<boolean>('registrar', subscription);

    const {
        state: { value: staff },
        context: { setValue: setStaff }
    } = useScopedField<boolean>('staff', subscription);

    const {
        state: { value: attendeeFromJapanOrKorea },
        context: { setValue: setAttendeeFromJapanOrKorea }
    } = useScopedField<boolean>('attendeeFromJapanOrKorea', subscription);

    const {
        state: { value: registrationType },
        context: { setValue: setRegistrationType }
    } = useScopedField<number>('registrationType', subscription);

    useScopedField<boolean>('staffConfirmed', subscription);

    useEffect(() => {
        switch (registrationType) {
            case NEXT_YEAR_EARLY_REGISTRATION_TYPE:
                // Keep the early-registration type fixed, but let the trial coordinator
                // role stay opt-in so the package query can expose the TC-only packages.
                setDefaultUser(trialCoordinator !== true, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
            case REGISTRATION_TYPE_DEFAULT:
                setDefaultUser(true, true, true, true, false);
                setTrialCoordinator(false, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
            case REGISTRATION_TYPE_STUDENT:
                setDefaultUser(false, true, true, true, false);
                setTrialCoordinator(false, true, true, true, false);
                setStudent(true, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
            case REGISTRATION_TYPE_TRIAL_COORDINATOR:
                setDefaultUser(false, true, true, true, false);
                setTrialCoordinator(true, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
            case REGISTRATION_TYPE_REGISTRAR:
                setDefaultUser(false, true, true, true, false);
                setTrialCoordinator(false, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(true, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
            case REGISTRATION_TYPE_STAFF:
                setDefaultUser(false, true, true, true, false);
                setTrialCoordinator(false, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(true, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
            case REGISTRATION_TYPE_ATTENDEE_FROM_JAPAN_OR_KOREA:
                setDefaultUser(false, true, true, true, false);
                setTrialCoordinator(false, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(true, true, true, true, false);
                break;
            default:
                setDefaultUser(true, true, true, true, false);
                setTrialCoordinator(false, true, true, true, false);
                setStudent(false, true, true, true, false);
                setRegistrar(false, true, true, true, false);
                setStaff(false, true, true, true, false);
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
                break;
        }
    }, [registrationType]);

    useEffect(() => {
        if (isNextYearEarlyRegistration) {
            // Prevent standard registration toggles from overriding the next-year early registration type.
            if (registrationType !== NEXT_YEAR_EARLY_REGISTRATION_TYPE) {
                setRegistrationType(NEXT_YEAR_EARLY_REGISTRATION_TYPE, true, true, true, false);
            }

            const shouldBeDefaultUser = trialCoordinator !== true;

            if (defaultUser !== shouldBeDefaultUser) {
                setDefaultUser(shouldBeDefaultUser, true, true, true, false);
            }

            if (student) {
                setStudent(false, true, true, true, false);
            }

            if (staff) {
                setStaff(false, true, true, true, false);
            }

            if (registrar) {
                setRegistrar(false, true, true, true, false);
            }

            if (attendeeFromJapanOrKorea) {
                setAttendeeFromJapanOrKorea(false, true, true, true, false);
            }

            return;
        }

        if (trialCoordinator) {
            setRegistrationType(REGISTRATION_TYPE_TRIAL_COORDINATOR, true, true, true, false);
            setStudent(false, true, true, true, false);
            setRegistrar(false, true, true, true, false);
            setStaff(false, true, true, true, false);
            setDefaultUser(false, true, true, true, false);
            setAttendeeFromJapanOrKorea(false, true, true, true, false);
        }
        else if (student) {
            setRegistrationType(REGISTRATION_TYPE_STUDENT, true, true, true, false);
            setTrialCoordinator(false, true, true, true, false);
            setRegistrar(false, true, true, true, false);
            setStaff(false, true, true, true, false);
            setDefaultUser(false, true, true, true, false);
            setAttendeeFromJapanOrKorea(false, true, true, true, false);
        }
        else if (staff) {
            setRegistrationType(REGISTRATION_TYPE_STAFF, true, true, true, false);
            setStudent(false, true, true, true, false);
            setRegistrar(false, true, true, true, false);
            setTrialCoordinator(false, true, true, true, false);
            setDefaultUser(false, true, true, true, false);
            setAttendeeFromJapanOrKorea(false, true, true, true, false);
        }
        else if (registrar) {
            setRegistrationType(REGISTRATION_TYPE_REGISTRAR, true, true, true, false);
            setStudent(false, true, true, true, false);
            setTrialCoordinator(false, true, true, true, false);
            setStaff(false, true, true, true, false);
            setDefaultUser(false, true, true, true, false);
            setAttendeeFromJapanOrKorea(false, true, true, true, false);
        }
        else if (attendeeFromJapanOrKorea) {
            setRegistrationType(REGISTRATION_TYPE_ATTENDEE_FROM_JAPAN_OR_KOREA, true, true, true, false);
            setStudent(false, true, true, true, false);
            setTrialCoordinator(false, true, true, true, false);
            setStaff(false, true, true, true, false);
            setRegistrar(false, true, true, true, false);
            setDefaultUser(false, true, true, true, false);
        }
        else if (defaultUser) {
            setRegistrationType(REGISTRATION_TYPE_DEFAULT, true, true, true, false);
            setStudent(false, true, true, true, false);
            setTrialCoordinator(false, true, true, true, false);
            setStaff(false, true, true, true, false);
            setRegistrar(false, true, true, true, false);
            setAttendeeFromJapanOrKorea(false, true, true, true, false);
        }
        else {
            setRegistrationType(REGISTRATION_TYPE_DEFAULT, true, true, true, false);
            setStudent(false, true, true, true, false);
            setTrialCoordinator(false, true, true, true, false);
            setStaff(false, true, true, true, false);
            setRegistrar(false, true, true, true, false);
            setAttendeeFromJapanOrKorea(false, true, true, true, false);
        }
    }, [isNextYearEarlyRegistration, trialCoordinator, student, staff, registrar, defaultUser, attendeeFromJapanOrKorea]);

    return null;
};

export default RegistrationTypeUpdater;
